import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Envelope, CaretDown, Buildings, Phone, MapPin } from "@phosphor-icons/react";
import { SPALink } from "../context/NavigationContext";

const Footer = () => {
  const { t } = useTranslation();
  const [showCompany, setShowCompany] = useState(false);

  return (
    <footer className="relative py-16 overflow-hidden border-t border-white/10">
      <div className="absolute inset-0 bg-lumina-dark" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-[1.3fr_0.8fr_0.8fr_0.8fr] gap-8 md:gap-16 mb-12">
          <div>
            <SPALink to="/" className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-full overflow-hidden">
                <img src="/images/lumina-logo.png" alt="Lumina" className="w-full h-full object-cover" />
              </div>
              <span className="text-2xl font-bold text-white">Lumina</span>
            </SPALink>
            <p className="text-gray-400 text-sm mb-4 leading-relaxed">
              {t("footer.brandDescription")}
            </p>

            <div className="space-y-2 mb-4">
              <a
                href="mailto:support@luminawallet.org"
                className="flex items-center gap-2 text-gray-400 hover:text-lumina-violet transition-colors text-sm"
                data-testid="footer-email"
              >
                <Envelope size={16} weight="duotone" />
                <span>support@luminawallet.org</span>
              </a>
              <a
                href="tel:+18437328996"
                className="flex items-center gap-2 text-gray-400 hover:text-lumina-violet transition-colors text-sm"
                data-testid="footer-phone"
              >
                <Phone size={16} weight="duotone" />
                <span>+1 843 732 8996</span>
              </a>
            </div>
          </div>

          <div>
            <h4 className="text-white font-semibold mb-4">{t("footer.product")}</h4>
            <ul className="space-y-3">
              <li><SPALink to="/features" className="text-gray-400 hover:text-lumina-violet transition-colors text-sm">{t("nav.features")}</SPALink></li>
              <li><SPALink to="/download" className="text-gray-400 hover:text-lumina-violet transition-colors text-sm">{t("nav.download")}</SPALink></li>
              <li><SPALink to="/blog" className="text-gray-400 hover:text-lumina-violet transition-colors text-sm" data-testid="footer-blog-link">{t("nav.blog")}</SPALink></li>
              <li><SPALink to="/faq" className="text-gray-400 hover:text-lumina-violet transition-colors text-sm">{t("nav.faq")}</SPALink></li>
              <li><SPALink to="/about" className="text-gray-400 hover:text-lumina-violet transition-colors text-sm" data-testid="footer-about-link">{t("nav.about")}</SPALink></li>
              <li><SPALink to="/trust-security" className="text-gray-400 hover:text-lumina-violet transition-colors text-sm" data-testid="footer-trust-link">Trust & Security</SPALink></li>
            </ul>
          </div>

          <div>
            <button
              onClick={() => setShowCompany(!showCompany)}
              className="flex items-center gap-2 text-white font-semibold mb-4 hover:text-lumina-violet transition-colors"
              data-testid="footer-company-toggle"
            >
              <span>{t("footer.aboutUs")}</span>
              <CaretDown size={14} weight="bold" className={`transition-transform duration-300 ${showCompany ? 'rotate-180' : ''}`} />
            </button>
            <div className={`overflow-hidden transition-all duration-300 ${showCompany ? 'max-h-60 opacity-100' : 'max-h-0 opacity-0'}`}>
              <ul className="space-y-2">
                <li className="text-gray-400 text-sm">HEDONISME SARL</li>
                <li className="text-gray-400 text-sm">RCS Dunkerque 901 439 505</li>
                <li className="text-gray-400 text-sm">{t("footer.capital")} : 64 000 €</li>
                <li className="text-gray-400 text-sm flex items-start gap-1.5 mt-2">
                  <MapPin size={14} weight="duotone" className="mt-0.5 flex-shrink-0" />
                  <span>933 Route Départementale, la Houblonnière, 59270 Méteren, France</span>
                </li>
              </ul>
            </div>
            {!showCompany && (
              <a href="mailto:support@luminawallet.org" className="text-gray-400 hover:text-lumina-violet transition-colors text-sm">
                {t("footer.contactUs")}
              </a>
            )}
          </div>

          <div>
            <h4 className="text-white font-semibold mb-4">{t("footer.legal")}</h4>
            <ul className="space-y-3">
              <li><SPALink to="/mentions-legales" className="text-gray-400 hover:text-lumina-violet transition-colors text-sm">{t("footer.legalNotice")}</SPALink></li>
              <li><SPALink to="/politique-confidentialite" className="text-gray-400 hover:text-lumina-violet transition-colors text-sm">{t("footer.privacy")}</SPALink></li>
              <li><SPALink to="/conditions-utilisation" className="text-gray-400 hover:text-lumina-violet transition-colors text-sm">{t("footer.terms")}</SPALink></li>
            </ul>
          </div>
        </div>

        <div className="pt-8 border-t border-white/10">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2 text-gray-500 text-sm">
              <Buildings size={16} weight="duotone" />
              <span>{t("footer.developedBy")}</span>
            </div>
            <p className="text-gray-600 text-sm">
              © {new Date().getFullYear()} Lumina Wallet. {t("footer.copyright")}
            </p>
          </div>
          <p className="mt-6 text-gray-500 text-xs leading-relaxed max-w-4xl" data-testid="footer-disclaimer">
            {t("footer.disclaimer")}
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
